import React, { useState } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Plus, Check, X } from 'lucide-react'

interface EditableDropdownProps {
  value: string
  onChange?: (value: string) => void
  onSave?: (value: string) => void
  options: Array<{ value: string; label: string }> | string[]
  placeholder?: string
  className?: string
  allowCreate?: boolean
}

export function EditableDropdown({ value, onChange, onSave, options, placeholder, className, allowCreate = true }: EditableDropdownProps) {
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [newValue, setNewValue] = useState('')

  // Normalize options to ensure consistent format
  const normalizedOptions = Array.isArray(options) && options.length > 0 && typeof options[0] === 'object' 
    ? options as Array<{ value: string; label: string }>
    : (options as string[]).map(opt => ({ value: opt, label: opt }))

  const handleSave = onChange || onSave || (() => {})

  const handleAddNew = () => {
    if (newValue.trim()) {
      handleSave(newValue.trim())
      setNewValue('')
      setIsAddingNew(false)
    }
  }

  const handleCancelAdd = () => {
    setNewValue('')
    setIsAddingNew(false)
  }

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === "__add_new__") {
      setIsAddingNew(true)
    } else {
      handleSave(selectedValue)
    }
  }

  if (isAddingNew) {
    return (
      <div className="flex items-center gap-2 w-full">
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="Enter new value"
          className="flex-1"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddNew()
            } else if (e.key === 'Escape') {
              handleCancelAdd()
            }
          }}
        />
        <Button size="sm" onClick={handleAddNew} disabled={!newValue.trim()}>
          <Check className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={handleCancelAdd}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <Select value={value} onValueChange={handleSelectChange}>
      <SelectTrigger className={`bg-white z-50 ${className || ''}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-white z-50">
        {normalizedOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {allowCreate && (
          <SelectItem value="__add_new__">
            <div className="flex items-center gap-2 text-primary">
              <Plus className="h-4 w-4" />
              Add new...
            </div>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  )
}